import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BlogService, BlogPost, BlogCategory, Language } from '@services/blog.service';
import { LanguageService } from '@services/language.service';
import { ToastrService } from 'ngx-toastr';
import { UploadService } from '@services/upload.service';

@Component({
  selector: 'app-blog-edit',
  templateUrl: './blog-edit.component.html',
  styleUrls: ['./blog-edit.component.css']
})
export class BlogEditComponent implements OnInit {
  @Input() isEditMode = false;
  @Input() postData!: BlogPost;

  blogForm: FormGroup;
  categories: BlogCategory[] = [];
  languages: Language[] = [];
  submitted = false;
  currentUserId = 1;

  files: File[] = [];
  featuredImageUrl = '';

  editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image', 'video']
    ]
  };

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private blogService: BlogService,
    private languageService: LanguageService,
    private toastr: ToastrService,
    private uploadService: UploadService
  ) {}

  ngOnInit(): void {
    console.log('BlogEditComponent initialized with postData:', this.postData);
    this.loadLanguages();
    this.loadCategories();
    this.initForm();

    if (this.isEditMode && this.postData) {
      this.patchFormValues();
    }
  }

  initForm(): void {
    this.blogForm = this.fb.group({
      slug: ['', Validators.required],
      languageId: ['', Validators.required],
      blogCategoryId: ['', Validators.required],
      title: ['', Validators.required],
      summary: ['', Validators.required],
      content: ['', Validators.required],
      metaTitle: [''],
      metaDescription: [''],
      metaKeywords: [''],
      featuredImage: [''],
      isPublished: [false],
      isFeatured: [false],
      tags: ['']
    });
  }

  loadLanguages(): void {
    this.languageService.all().subscribe({
      next: (languages) => { this.languages = languages; },
      error: () => { this.toastr.error('Diller yüklenirken hata oluştu', 'Hata'); }
    });
  }

  loadCategories(): void {
    this.blogService.getCategories().subscribe({
      next: (categories) => { this.categories = categories; },
      error: () => { this.toastr.error('Kategoriler yüklenirken hata oluştu', 'Hata'); }
    });
  }

  patchFormValues(): void {
    this.blogForm.patchValue({
      slug: this.postData.slug,
      languageId: this.postData.languageId,
      blogCategoryId: this.postData.blogCategoryId,
      title: this.postData.title,
      summary: this.postData.summary,
      content: this.postData.content,
      metaTitle: this.postData.metaTitle,
      metaDescription: this.postData.metaDescription,
      metaKeywords: this.postData.metaKeywords,
      featuredImage: this.postData.featuredImage,
      isPublished: this.postData.isPublished,
      isFeatured: this.postData.isFeatured,
      tags: this.postData.tags.join(', ')
    });

    this.featuredImageUrl = this.postData.featuredImage;
  }

  onFileSelect(event: any): void {
    this.files = [];
    this.files.push(...event.addedFiles);

    if (this.files[0].size > 2048000) {
      this.toastr.warning('2 MB\'dan büyük dosya yükleyemezsiniz.', 'Uyarı');
      this.files = [];
      return;
    }

    const formData = new FormData();
    formData.append('file', this.files[0], this.files[0].name);

    this.uploadService.file(formData, 'blog').subscribe({
      next: (res: string[]) => {
        this.featuredImageUrl = res[0];
        this.blogForm.get('featuredImage').patchValue(res[0]);
        this.toastr.success('Görsel başarıyla yüklendi', 'Başarılı');
      },
      error: () => {
        this.toastr.error('Görsel yüklenirken hata oluştu.', 'Hata');
        this.files = [];
      }
    });
  }

  onFileRemove(): void {
    this.files = [];
    this.featuredImageUrl = '';
    this.blogForm.get('featuredImage').patchValue('');
  }

  save(): void {
    this.submitted = true;

    if (!this.blogForm.valid) {
      this.blogForm.markAllAsTouched();
      this.toastr.warning('Lütfen gerekli alanları doldurunuz', 'Uyarı');
      return;
    }

    const formValue = this.blogForm.value;
    const tagsArray = formValue.tags ? formValue.tags.split(',').map(t => t.trim()).filter(t => t) : [];

    const blogPost: BlogPost = {
      slug: formValue.slug,
      languageId: formValue.languageId,
      blogCategoryId: formValue.blogCategoryId,
      title: formValue.title,
      summary: formValue.summary,
      content: formValue.content,
      metaTitle: formValue.metaTitle,
      metaDescription: formValue.metaDescription,
      metaKeywords: formValue.metaKeywords,
      authorId: this.currentUserId,
      featuredImage: formValue.featuredImage,
      isPublished: formValue.isPublished,
      isFeatured: formValue.isFeatured,
      tags: tagsArray
    };

    if (this.isEditMode) {
      blogPost.id = this.postData.id;
      this.blogService.updatePost(this.postData.id!, blogPost).subscribe({
        next: () => {
          this.toastr.success('Blog yazısı başarıyla güncellendi', 'Başarılı');
          this.activeModal.close(true);
        },
        error: () => {
          this.toastr.error('Blog yazısı güncellenirken hata oluştu', 'Hata');
          this.submitted = false;
        }
      });
    } else {
      this.blogService.createPost(blogPost).subscribe({
        next: () => {
          this.toastr.success('Blog yazısı başarıyla oluşturuldu', 'Başarılı');
          this.activeModal.close(true);
        },
        error: () => {
          this.toastr.error('Blog yazısı oluşturulurken hata oluştu', 'Hata');
          this.submitted = false;
        }
      });
    }
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
